import type { ReactNode } from 'react';
import { Button } from './Button';
import { Modal, type ModalIntent } from './Modal';

export interface ConfirmDialogProps {
  open: boolean;
  /**
   * タイトル。Modal.title に渡され、intent に応じてアイコン + 色が付く。
   */
  title: ReactNode;
  /**
   * 本文。文字列なら `<p>` でラップ、ReactNode ならそのまま挿入。
   */
  message: ReactNode;
  /** 確認 button のラベル。デフォルト「確認」。 */
  confirmLabel?: string;
  /** キャンセル button のラベル。デフォルト「キャンセル」。 */
  cancelLabel?: string;
  /**
   * intent。デフォルト 'warning'。
   * - warning: 通常の確認 (リセット等、可逆 / 軽い破壊的操作)
   * - danger: より破壊的な操作 (削除等、確認 button が danger variant)
   * - default: 通常確認 (アイコン無し)
   */
  intent?: ModalIntent;
  /** 確認 button 押下時のコールバック。 */
  onConfirm: () => void;
  /** キャンセル button / × / Esc / overlay click 時のコールバック。 */
  onCancel: () => void;
  /**
   * focus trap を有効にするか。Sprint BA (a11y 横断) で追加。default true。
   * 既存 consumer は引数省略で動くため後方互換。
   */
  trapFocus?: boolean;
}

/**
 * 確認ダイアログ (Sprint AO、docs/16 §10-2 P2 H7 UI4 設定リセット導線で新規追加)。
 *
 * 「タイトル + 本文 + キャンセル / 確認」 2 button の小型 modal。
 * 設定リセット系、削除系、その他破壊的操作の二段階確認用。
 *
 * intent prop は Modal.intent と同じ意味:
 *   - 'warning' (default): 黄系ヘッダ + ⚠️、確認 button は primary
 *   - 'danger': 赤系ヘッダ + ⚠️、確認 button は danger variant
 *   - 'default': アイコン無し、確認 button は primary
 *
 * Sprint AN の「明示 Save button pattern」と整合 — overlay click / Esc / × はキャンセル扱いで、
 * 副作用は確認 button のみが発火させる。
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '確認',
  cancelLabel = 'キャンセル',
  intent = 'warning',
  onConfirm,
  onCancel,
  trapFocus = true,
}: ConfirmDialogProps) {
  const confirmVariant = intent === 'danger' ? 'danger' : 'primary';
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      intent={intent}
      trapFocus={trapFocus}
    >
      {typeof message === 'string' ? (
        <p className="text-sm text-slate-700 mb-6 whitespace-pre-line">{message}</p>
      ) : (
        <div className="text-sm text-slate-700 mb-6">{message}</div>
      )}
      <div className="flex gap-2 justify-end">
        <Button variant="secondary" size="md" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant={confirmVariant} size="md" onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
